import type { RequiredGeneric } from '@pba/react-utils';
import type { LabelInValue, NodeMembership } from 'models';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, OptionSelect } from 'ui';
import { MedicalGroupPicker, useMedicalGroupPickerCreateLabelInValueOption } from 'features/medical-groups';
import type { LabelInValueWithTranslations } from '../../../../types';
import { visitTypeOptions } from './visit-type-options';

type P = {
  userMedicalGroupMemberships?: NodeMembership[];
};

type TelehealthOptionsFormValues = {
  medicalGroup?: LabelInValue;
  visitType: LabelInValueWithTranslations;
  visitTypeSubOption?: LabelInValueWithTranslations;
  visitTypeSubSubOption?: LabelInValueWithTranslations;
};

const getOptionLabel = ({
  language,
  option,
}: {
  language: string;
  option: { name: string; translations: Record<string, string> };
}) => option.translations[language] ?? option.name;

const TelehealthOptions = <T extends TelehealthOptionsFormValues = never>({
  userMedicalGroupMemberships,
}: RequiredGeneric<T, P>) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const medicalGroupPickerCreateLabelInValueOption = useMedicalGroupPickerCreateLabelInValueOption();
  const hasOnlyOneMedicalGroup = (userMedicalGroupMemberships?.length ?? 0) === 1;
  const form = Form.useFormInstance<TelehealthOptionsFormValues>();

  const visitType = Form.useWatch('visitType', form) as TelehealthOptionsFormValues['visitType'] | undefined;
  const visitTypeSubOption = Form.useWatch('visitTypeSubOption', form);

  const visitTypeSelectOptions = useMemo(
    () =>
      visitTypeOptions.map((option) => ({
        children: option.children,
        label: getOptionLabel({ language, option }),
        translations: option.translations,
        value: option.name,
      })),
    [language],
  );

  const visitTypeSubSelectOptions = useMemo(
    () =>
      visitType?.children?.map((option) => ({
        children: option.children,
        label: getOptionLabel({ language, option }),
        translations: option.translations,
        value: option.name,
      })),
    [language, visitType],
  );

  const visitTypeSubSubSelectOptions =
    useMemo(
      () =>
        visitTypeSubOption?.children?.map((option) => ({
          children: option.children,
          label: getOptionLabel({ language, option }),
          translations: option.translations,
          value: option.name,
        })),
      [language, visitTypeSubOption],
    ) ?? [];

  useEffect(() => {
    form.setFieldsValue({
      visitTypeSubOption: undefined,
      visitTypeSubSubOption: undefined,
    });
  }, [visitType, form]);

  useEffect(() => {
    form.setFieldsValue({
      visitTypeSubSubOption: undefined,
    });
  }, [visitTypeSubOption, form]);

  return (
    <>
      <Form.Item<TelehealthOptionsFormValues>
        hidden={!(userMedicalGroupMemberships && userMedicalGroupMemberships.length > 1)}
        label="Medical Group"
        name="medicalGroup"
        rules={[{ required: !hasOnlyOneMedicalGroup }]}
      >
        <MedicalGroupPicker
          allowClear={false}
          createOption={medicalGroupPickerCreateLabelInValueOption}
          query={{ filter: { ids: userMedicalGroupMemberships?.map((membership) => membership.ref) } }}
        />
      </Form.Item>
      <Form.Item<TelehealthOptionsFormValues> label="Visit Type" name="visitType" rules={[{ required: true }]}>
        <OptionSelect options={visitTypeSelectOptions} placeholder="Choose type..." showSearch />
      </Form.Item>
      {visitType ? (
        <Form.Item<TelehealthOptionsFormValues> label="Visit type sub-option" name="visitTypeSubOption">
          <OptionSelect allowClear options={visitTypeSubSelectOptions} placeholder="Choose sub-option..." showSearch />
        </Form.Item>
      ) : null}
      {visitTypeSubSubSelectOptions.length > 0 ? (
        <Form.Item<TelehealthOptionsFormValues>
          label={`${typeof visitTypeSubOption?.label === 'string' ? visitTypeSubOption.label : ''} ${t('sub-option')}`}
          name="visitTypeSubSubOption"
        >
          <OptionSelect
            allowClear
            options={visitTypeSubSubSelectOptions}
            placeholder="Choose sub-option..."
            showSearch
          />
        </Form.Item>
      ) : null}
    </>
  );
};

export default TelehealthOptions;
